import React, { useCallback, useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import {
    createSendingDomainSchema,
    sendingDomainSchema,
    type SendingDomainResponse,
} from "@common/schemas/sending-domain.schema";
import { SendingDomainMethod } from "@common/types/domains/emailSending";
import { useCreateSendingDomain } from "@api/sendingDomain.api";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { Input } from "@design-system/Input/Input";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { VStack } from "@design-system/VStack/VStack";
import { validateFormikWithZodSchema } from "@helpers/form";
import { HOTKEYS_SCOPES } from "@hooks/keyboard/useKeyboardCommand";
import { DnsRecordsPanel } from "./DnsRecordsPanel";
import {
    sendingDomainMethodLabel,
    smtpSecureForWellKnownPort,
} from "./sendingDomainHelpers";
import { HStack } from "designSystem/HStack/HStack";

const smtpPortSchema = z.coerce.number().int().min(1).max(65535);

const addSendingDomainFormSchema = z.discriminatedUnion("method", [
    z.object({
        method: z.literal(SendingDomainMethod.dns),
        domain: sendingDomainSchema,
        smtpHost: z.string(),
        smtpPort: smtpPortSchema,
        smtpSecure: z.boolean(),
        smtpUsername: z.string(),
        smtpPassword: z.string(),
    }),
    z.object({
        method: z.literal(SendingDomainMethod.smtp),
        domain: sendingDomainSchema,
        smtpHost: z.string().trim().min(1, "Host is required").max(253),
        smtpPort: smtpPortSchema,
        smtpSecure: z.boolean(),
        smtpUsername: z.string().trim().min(1, "Username is required").max(320),
        smtpPassword: z.string().min(1, "Password is required").max(1024),
    }),
]);

interface AddSendingDomainFormValues {
    method: SendingDomainMethod;
    domain: string;
    smtpHost: string;
    smtpPort: number | string;
    smtpSecure: boolean;
    smtpUsername: string;
    smtpPassword: string;
}

const fields = createFormikFields<AddSendingDomainFormValues>();

const methodOptions = [SendingDomainMethod.dns, SendingDomainMethod.smtp].map(
    (method) => ({ value: method, label: sendingDomainMethodLabel(method) }),
);

const emptyValues: AddSendingDomainFormValues = {
    method: SendingDomainMethod.dns,
    domain: "",
    smtpHost: "",
    smtpPort: 587,
    smtpSecure: false,
    smtpUsername: "",
    smtpPassword: "",
};

interface AddSendingDomainModalProps {
    show: boolean;
    initialDomain?: string;
    onClose: () => void;
}

export const AddSendingDomainModal: React.FC<AddSendingDomainModalProps> = ({
    show,
    initialDomain = "",
    onClose,
}) => {
    const createDomain = useCreateSendingDomain();
    const { toast } = useToaster();
    const [createdDnsDomain, setCreatedDnsDomain] =
        useState<SendingDomainResponse | null>(null);

    const formik = useFormik<AddSendingDomainFormValues>({
        initialValues: { ...emptyValues, domain: initialDomain },
        enableReinitialize: true,
        validate: validateFormikWithZodSchema(addSendingDomainFormSchema),
        validateOnMount: true,
        onSubmit: async (values, helpers) => {
            try {
                const created = await createDomain.mutateAsync(
                    toCreateRequest(values),
                );
                if (created.method === SendingDomainMethod.dns) {
                    setCreatedDnsDomain(created);
                    helpers.resetForm();
                    return;
                }
                toast("Domain verified");
                helpers.resetForm();
                onClose();
            } catch {
                helpers.setSubmitting(false);
            }
        },
    });

    const handleClose = useCallback(() => {
        formik.resetForm();
        setCreatedDnsDomain(null);
        onClose();
    }, [formik, onClose]);

    const isDnsMethod = formik.values.method === SendingDomainMethod.dns;
    const isSmtpMethod = formik.values.method === SendingDomainMethod.smtp;

    return (
        <PlainModal
            show={show}
            onHide={handleClose}
            size={createdDnsDomain ? "lg" : "md"}
        >
            {createdDnsDomain ? (
                <>
                    <StandardModalHeader>Add DNS records</StandardModalHeader>
                    <DnsRecordsPanel
                        key={createdDnsDomain.id}
                        domain={createdDnsDomain}
                        onVerified={handleClose}
                        onDismiss={handleClose}
                    />
                </>
            ) : (
                <FormikProvider value={formik}>
                    <Form>
                        <StandardModalHeader>
                            Add sending domain
                        </StandardModalHeader>
                        <VStack gap={16}>
                            <VStack gap={16}>
                                <fields.Select
                                    name="method"
                                    label="Verification method"
                                    options={methodOptions}
                                    placeholder="Select a method"
                                    buttonProps={{ size: "xl", block: true }}
                                />
                                <fields.Text
                                    name="domain"
                                    label="Domain"
                                    placeholder="acme.com"
                                    labelTooltip="Lowercase hostname only, with no scheme or path. International domains must be punycode."
                                    size="xl"
                                    required
                                />
                            </VStack>
                            {isSmtpMethod && (
                                <VStack gap={16}>
                                    <fields.Text
                                        name="smtpHost"
                                        label="SMTP host"
                                        placeholder="smtp.example.com"
                                        size="xl"
                                        required
                                    />
                                    <FormikField<number | string>
                                        name="smtpPort"
                                        label="Port"
                                    >
                                        {({ field, helpers, invalid }) => (
                                            <Input
                                                name={field.name}
                                                value={field.value}
                                                onBlur={() => {
                                                    void helpers.setTouched(
                                                        true,
                                                    );
                                                }}
                                                type="number"
                                                size="xl"
                                                invalid={invalid}
                                                aria-required
                                                onChange={(event) => {
                                                    applySmtpPortChange(
                                                        formik.setFieldValue,
                                                        event.target.value,
                                                    );
                                                }}
                                            />
                                        )}
                                    </FormikField>
                                    <VStack gap={16}>
                                        <fields.Switch
                                            name="smtpSecure"
                                            label="TLS"
                                            description="Port 465 uses implicit TLS. Port 587 uses STARTTLS, so leave this off."
                                        />
                                        <fields.Text
                                            name="smtpUsername"
                                            label="Username"
                                            size="xl"
                                            required
                                        />
                                    </VStack>
                                    <fields.Text
                                        name="smtpPassword"
                                        label="Password"
                                        type="password"
                                        size="xl"
                                        required
                                    />
                                </VStack>
                            )}
                            <HStack justify="end" gap={4}>
                                <Button
                                    variant="secondary"
                                    size="md"
                                    type="button"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </Button>
                                <ButtonWithLoader
                                    variant="primary"
                                    size="md"
                                    type="submit"
                                    hotKey="mod+enter"
                                    hotKeyOptions={{
                                        description: "Add sending domain",
                                        preventDefault: true,
                                        enableOnFormTags: true,
                                        scopes: [HOTKEYS_SCOPES.SETTINGS_MODAL],
                                    }}
                                    hotKeyDisplayPosition="inline"
                                    loading={formik.isSubmitting}
                                    disabled={!formik.isValid}
                                >
                                    {isDnsMethod ? "Add domain" : "Verify SMTP"}
                                </ButtonWithLoader>
                            </HStack>
                        </VStack>
                    </Form>
                </FormikProvider>
            )}
        </PlainModal>
    );
};

function toCreateRequest(values: AddSendingDomainFormValues) {
    if (values.method === SendingDomainMethod.dns) {
        return createSendingDomainSchema.parse({
            method: SendingDomainMethod.dns,
            domain: values.domain,
        });
    }
    return createSendingDomainSchema.parse({
        method: SendingDomainMethod.smtp,
        domain: values.domain,
        smtpHost: values.smtpHost,
        smtpPort: Number(values.smtpPort),
        smtpSecure: values.smtpSecure,
        smtpUsername: values.smtpUsername,
        smtpPassword: values.smtpPassword,
    });
}

function applySmtpPortChange(
    setFieldValue: (field: string, value: number | string | boolean) => void,
    value: string,
) {
    const smtpPort = value === "" ? "" : Number(value);
    setFieldValue("smtpPort", smtpPort);
    if (typeof smtpPort !== "number" || Number.isNaN(smtpPort)) {
        return;
    }
    const smtpSecure = smtpSecureForWellKnownPort(smtpPort);
    if (smtpSecure !== undefined) {
        setFieldValue("smtpSecure", smtpSecure);
    }
}
